import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { AdminService } from '../../services/AdminService/admin';
import { CompanyResponse, CompanyStatus } from '../../models/company.dto';
import { extractErrorMessage } from '../../shared/error-utils';
import { StatusBadge } from '../../shared/components/status-badge/status-badge';
import { ErrorBanner } from '../../shared/components/error-banner/error-banner';
import { EmptyState } from '../../shared/components/empty-state/empty-state';
import { SuspendCompanyDialog } from '../../dialogs/suspend-company-dialog/suspend-company-dialog';
import { RejectCompanyDialog } from '../../dialogs/reject-company-dialog/reject-company-dialog';
import { PageSizeSelect } from '../../shared/components/page-size-select/page-size-select';

@Component({
  selector: 'app-company-approval-queue',
  imports: [CommonModule, FormsModule, RouterLink, StatusBadge, ErrorBanner, EmptyState, PageSizeSelect],
  templateUrl: './company-approval-queue.html',
  styleUrl: './company-approval-queue.scss',
})
export class CompanyApprovalQueue implements OnInit {
  statusFilter = signal<CompanyStatus | ''>('PENDING');

  companies = signal<CompanyResponse[]>([]);
  isLoading = signal(false);
  errorMessage = signal('');

  readonly pageSizeOptions = [10, 20, 50, 100];
  pageSize = signal(10);
  page = signal(0);
  totalPages = signal(1);
  totalCount = signal(0);

  constructor(
    private admin: AdminService,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog
  ) {}

  get rangeStart(): number {
    return this.totalCount() === 0 ? 0 : this.page() * this.pageSize() + 1;
  }

  get rangeEnd(): number {
    return Math.min((this.page() + 1) * this.pageSize(), this.totalCount());
  }

  get statusFilterLabel(): string {
    const status = this.statusFilter();
    if (!status) {
      return 'All statuses';
    }
    return status.charAt(0) + status.slice(1).toLowerCase();
  }

  /** Page numbers to render, with -1 as an ellipsis placeholder, e.g. [0, -1, 4, 5, 6, -1, 19]. */
  get pageWindow(): number[] {
    const total = this.totalPages();
    const current = this.page();
    const pages: number[] = [];
    const add = (p: number) => {
      if (pages[pages.length - 1] !== p) {
        pages.push(p);
      }
    };

    add(0);
    for (let p = current - 1; p <= current + 1; p++) {
      if (p > 0 && p < total - 1) {
        if (p > pages[pages.length - 1] + 1) {
          add(-1);
        }
        add(p);
      }
    }
    if (total - 1 > pages[pages.length - 1] + 1) {
      add(-1);
    }
    if (total > 1) {
      add(total - 1);
    }
    return pages;
  }

  ngOnInit(): void {
    const statusParam = this.route.snapshot.queryParamMap.get('status');
    if (statusParam !== null) {
      this.statusFilter.set(statusParam as CompanyStatus | '');
    }
    this.load();
  }

  setFilter(status: CompanyStatus | ''): void {
    this.statusFilter.set(status);
    this.page.set(0);
    this.router.navigate([], { relativeTo: this.route, queryParams: { status }, replaceUrl: true });
    this.load();
  }

  onPageSizeChange(size: number): void {
    this.pageSize.set(size);
    this.page.set(0);
    this.load();
  }

  load(): void {
    this.isLoading.set(true);
    this.errorMessage.set('');

    this.admin.listCompanies(this.statusFilter() || undefined, this.page(), this.pageSize()).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        this.companies.set(res.data);
        this.totalPages.set(res.page?.total_pages ?? 1);
        this.totalCount.set(res.page?.count ?? res.data.length);
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading.set(false);
        this.errorMessage.set(extractErrorMessage(err, 'Failed to load companies.'));
      },
    });
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages() || page === this.page()) {
      return;
    }
    this.page.set(page);
    this.load();
  }

  approve(company: CompanyResponse): void {
    this.admin.approve(company.id).subscribe({
      next: () => this.load(),
      error: (err: HttpErrorResponse) => {
        this.errorMessage.set(extractErrorMessage(err, 'Failed to approve company.'));
      },
    });
  }

  reject(company: CompanyResponse): void {
    this.dialog
      .open(RejectCompanyDialog, {
        width: '480px',
        data: { companyId: company.id, companyName: company.name },
      })
      .afterClosed()
      .subscribe((rejected) => {
        if (rejected) {
          this.load();
        }
      });
  }

  suspend(company: CompanyResponse): void {
    this.dialog
      .open(SuspendCompanyDialog, {
        width: '480px',
        data: { companyId: company.id, companyName: company.name },
      })
      .afterClosed()
      .subscribe((suspended) => {
        if (suspended) {
          this.load();
        }
      });
  }

  reactivate(company: CompanyResponse): void {
    this.admin.reactivate(company.id).subscribe({
      next: () => this.load(),
      error: (err: HttpErrorResponse) => {
        this.errorMessage.set(extractErrorMessage(err, 'Failed to reactivate company.'));
      },
    });
  }
}
